import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * URL-synced tab state hook.
 * Reads/writes a URL search param to keep the active tab bookmarkable
 * and responsive to browser back/forward navigation.
 *
 * Drop-in replacement for useState<T> — same [value, setter] signature.
 */
export default function useTabState<T extends string>(
  key: string,
  validTabs: readonly T[],
  defaultTab: T,
): [T, (tab: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(key);
  const currentTab: T =
    raw !== null && (validTabs as readonly string[]).includes(raw)
      ? (raw as T)
      : defaultTab;

  const setTab = useCallback(
    (tab: T) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === defaultTab) {
            next.delete(key);
          } else {
            next.set(key, tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [key, defaultTab, setSearchParams],
  );

  return [currentTab, setTab];
}
